import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

const jobs = [
  {
    title: 'Senior Full Stack Developer',
    type: 'Full-time / Remote',
    department: 'Engineering',
    location: 'Remote (Global)',
    description: 'Lead end-to-end development for client projects using modern frameworks. Mentor junior developers and architect scalable solutions.',
    skills: ['React', 'Node.js', 'TypeScript', 'PostgreSQL', 'AWS'],
  },
  {
    title: 'DevOps Engineer',
    type: 'Full-time / Remote',
    department: 'Infrastructure',
    location: 'Remote (Global)',
    description: 'Build and maintain CI/CD pipelines, manage cloud infrastructure, and ensure high availability across client environments.',
    skills: ['Kubernetes', 'Terraform', 'Docker', 'Azure', 'GitHub Actions'],
  },
  {
    title: 'Mobile App Developer (React Native)',
    type: 'Contract / Remote',
    department: 'Engineering',
    location: 'Remote (Global)',
    description: 'Develop cross-platform mobile applications for iOS and Android. Collaborate with UX designers to deliver polished user experiences.',
    skills: ['React Native', 'iOS', 'Android', 'Firebase', 'REST APIs'],
  },
  {
    title: 'Technical Recruiter',
    type: 'Full-time / Remote',
    department: 'Talent',
    location: 'Remote (Global)',
    description: 'Source and vet top-tier engineering talent globally. Partner with hiring managers to understand technical requirements and culture fit.',
    skills: ['Sourcing', 'Technical Screening', 'ATS', 'LinkedIn Recruiter', 'Communication'],
  },
];

const benefits = [
  { icon: 'ri-earth-line', title: 'Fully Remote', desc: 'Work from anywhere in the world with flexible hours.' },
  { icon: 'ri-currency-line', title: 'Competitive Pay', desc: 'Above-market salaries with performance bonuses and raises.' },
  { icon: 'ri-heart-pulse-line', title: 'Health & Wellness', desc: 'Comprehensive health coverage and mental wellness stipends.' },
  { icon: 'ri-graduation-cap-line', title: 'Learning Budget', desc: 'Annual budget for courses, conferences, and certifications.' },
  { icon: 'ri-calendar-event-line', title: 'Generous PTO', desc: 'Unlimited PTO policy with mandatory minimums to prevent burnout.' },
  { icon: 'ri-team-line', title: 'Great Culture', desc: 'Inclusive team with regular virtual events and team offsites.' },
];

export default function Careers() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.05,
  });

  const [expandedJob, setExpandedJob] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'success' | 'error'>('success');
  const [coverMessage, setCoverMessage] = useState('');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    for (const [key, value] of formData.entries()) {
      data.append(key, value as string);
    }
    fetch(form.action, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: data.toString(),
    })
      .then(() => {
        setModalType('success');
        setShowModal(true);
        form.reset();
        setCoverMessage('');
      })
      .catch(() => {
        setModalType('error');
        setShowModal(true);
      });
  };

  const jobTitles = jobs.map((j) => j.title);

  return (
    <section id="careers" className="py-16 md:py-24 bg-gray-50 relative overflow-hidden" ref={ref}>
      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.85, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.85, y: 20 }}
              transition={{ duration: 0.4, type: 'spring', bounce: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl md:rounded-3xl p-8 md:p-12 max-w-md w-full shadow-2xl text-center"
            >
              {modalType === 'success' ? (
                <>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.15, ...springTransition }}
                    className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center bg-primary-500 rounded-full mx-auto mb-6 shadow-lg"
                  >
                    <motion.i
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: 'spring', bounce: 0.5 }}
                      className="ri-check-line text-4xl md:text-5xl text-white"
                    ></motion.i>
                  </motion.div>
                  <h3 className="text-2xl md:text-3xl font-bold text-navy-900 mb-3">
                    Application Received!
                  </h3>
                  <p className="text-base md:text-lg text-gray-600 mb-8 leading-relaxed">
                    Thank you for your interest. Our talent team will review your application and reach out within 3-5 business days.
                  </p>
                </>
              ) : (
                <>
                  <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center bg-red-500 rounded-full mx-auto mb-6 shadow-lg">
                    <motion.i
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: 'spring', bounce: 0.5 }}
                      className="ri-error-warning-line text-4xl md:text-5xl text-white"
                    ></motion.i>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-navy-900 mb-3">
                    Oops!
                  </h3>
                  <p className="text-base md:text-lg text-gray-600 mb-8 leading-relaxed">
                    Something went wrong. Please try again or email us directly at{' '}
                    <a href="mailto:careers@thoughtic.com" className="text-primary-600 hover:underline">careers@thoughtic.com</a>.
                  </p>
                </>
              )}
              <button
                onClick={() => setShowModal(false)}
                className="px-8 py-3 bg-primary-500 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
              >
                {modalType === 'success' ? 'Got It' : 'Try Again'}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Decorative blobs */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-primary-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
      <div className="absolute bottom-20 left-10 w-72 h-72 bg-amber-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-briefcase-line text-primary-600"></i>
            <span className="text-sm font-medium text-gray-700">Join Our Team</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Careers at
            <br />
            <span className="text-primary-600">Thoughtic Solutions</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            We are building the future of remote tech talent. Join a global team where your skills matter, your growth is prioritized, and your work makes an impact.
          </motion.p>
        </motion.div>

        {/* Benefits Grid */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3, ...springTransition }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-16 md:mb-12"
        >
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.35 + index * 0.06, ...springTransition }}
              className="bg-white rounded-xl md:rounded-2xl p-5 md:p-6 border border-gray-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300 group"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-primary-50 rounded-xl mb-3 md:mb-4 group-hover:bg-primary-500 group-hover:scale-110 transition-all duration-300">
                <i className={`${benefit.icon} text-lg md:text-xl text-primary-600 group-hover:text-white transition-colors duration-300`}></i>
              </div>
              <h3 className="text-base md:text-lg font-bold text-navy-900 mb-1 md:mb-2">{benefit.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{benefit.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Stat Badge Row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4, ...springTransition }}
          className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-12 md:mb-16 max-w-4xl mx-auto"
        >
          {[
            { icon: 'ri-briefcase-line', value: '4', label: 'Open Roles', accent: 'bg-primary-50 border-primary-100 text-primary-700' },
            { icon: 'ri-heart-pulse-line', value: '6', label: 'Benefits', accent: 'bg-emerald-50 border-emerald-100 text-emerald-700' },
            { icon: 'ri-earth-line', value: '100%', label: 'Remote', accent: 'bg-sky-50 border-sky-100 text-sky-700' },
            { icon: 'ri-graduation-cap-line', value: '₹20K', label: 'Learning Budget', accent: 'bg-amber-50 border-amber-100 text-amber-700' },
          ].map((stat, idx) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.45 + idx * 0.06, ...springTransition }}
              className={`flex items-center gap-2.5 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-xl border ${stat.accent}`}
            >
              <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-white/70 flex-shrink-0">
                <i className={`${stat.icon} text-base md:text-lg`}></i>
              </div>
              <div className="text-left">
                <div className="text-sm md:text-base font-bold leading-tight">{stat.value}</div>
                <div className="text-[10px] md:text-xs font-medium opacity-80">{stat.label}</div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Jobs + Form Row */}
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
          {/* Jobs List */}
          <div className="w-full lg:w-1/2 space-y-4 md:space-y-5">
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.35, ...springTransition }}
              className="text-xl md:text-2xl font-bold text-navy-900 mb-4 md:mb-6"
            >
              Open Positions
            </motion.h3>

            {jobs.map((job, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.4 + index * 0.08, ...springTransition }}
                className="bg-white rounded-xl md:rounded-2xl border border-gray-100 overflow-hidden hover:border-primary-200 hover:shadow-lg transition-all duration-300"
              >
                <button
                  onClick={() => setExpandedJob(expandedJob === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 md:p-6 text-left cursor-pointer"
                >
                  <div className="space-y-1">
                    <h4 className="text-base md:text-lg font-bold text-navy-900">{job.title}</h4>
                    <div className="flex flex-wrap items-center gap-2 md:gap-3 text-xs md:text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-map-pin-line"></i>
                        {job.location}
                      </span>
                      <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                      <span>{job.type}</span>
                      <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                      <span className="px-2 py-0.5 bg-gray-100 rounded-md text-gray-600">{job.department}</span>
                    </div>
                  </div>
                  <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-gray-50 text-navy-900 transition-transform duration-300 flex-shrink-0 ml-4">
                    <i className={`ri-arrow-down-s-line text-lg md:text-xl transition-transform duration-300 ${expandedJob === index ? 'rotate-180' : ''}`}></i>
                  </div>
                </button>

                <AnimatePresence>
                  {expandedJob === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 md:px-6 pb-5 md:pb-6 pt-0 border-t border-gray-50">
                        <p className="text-sm md:text-base text-gray-600 leading-relaxed mt-4 md:mt-5 mb-4 md:mb-5">
                          {job.description}
                        </p>
                        <div className="flex flex-wrap gap-2 mb-5 md:mb-6">
                          {job.skills.map((skill, sidx) => (
                            <span
                              key={sidx}
                              className="px-3 py-1 bg-primary-50 text-primary-700 text-xs md:text-sm font-medium rounded-full border border-primary-100"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                        <button
                          onClick={() => {
                            setExpandedJob(null);
                            document.getElementById('resume-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }}
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
                        >
                          Apply Now
                          <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300 inline-block"></i>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>

          {/* Resume Submission Form */}
          <motion.div
            id="resume-form"
            initial={{ opacity: 0, x: 40, scale: 0.95 }}
            animate={inView ? { opacity: 1, x: 0, scale: 1 } : {}}
            transition={{ ...springTransition, duration: 0.8, delay: 0.35 }}
            className="w-full lg:w-1/2 relative"
          >
            {/* Decorative corner accents */}
            <div className="absolute -top-3 -right-3 w-20 h-20 bg-primary-100/40 rounded-2xl -z-10 hidden lg:block"></div>
            <div className="absolute -bottom-3 -left-3 w-16 h-16 bg-amber-100/40 rounded-2xl -z-10 hidden lg:block"></div>

            {/* Floating stat badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.6, ...springTransition }}
              className="absolute -top-3 -left-3 md:-top-4 md:-left-4 z-10 bg-white rounded-xl shadow-lg border border-emerald-100 px-3 py-2 md:px-4 md:py-2.5 hidden lg:flex items-center gap-2"
            >
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
              <span className="text-xs font-bold text-navy-900">We are hiring!</span>
            </motion.div>

            <div className="bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 lg:p-10 shadow-xl border border-gray-100 sticky top-24">
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="mb-6 md:mb-8"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4">
                  <i className="ri-send-plane-line text-primary-600"></i>
                  <span className="text-sm font-medium text-gray-700">Submit Your Resume</span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-navy-900 mb-2">Apply for a Position</h3>
                <p className="text-sm md:text-base text-gray-600">
                  Do not see the perfect role? Send us your resume anyway — we are always looking for exceptional talent.
                </p>
              </motion.div>

              <form
                className="space-y-3 md:space-y-4"
                method="POST"
                action="https://readdy.ai/api/form/d8857k04og5fkb1kt0k0"
                encType="application/x-www-form-urlencoded"
                data-readdy-form
                onSubmit={handleSubmit}
              >
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.45, duration: 0.4 }}
                >
                  <label htmlFor="career-name" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="career-name"
                    name="name"
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm"
                    placeholder="John Doe"
                    required
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.48, duration: 0.4 }}
                >
                  <label htmlFor="career-email" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="career-email"
                    name="email"
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm"
                    placeholder="john@example.com"
                    required
                  />
                </motion.div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.51, duration: 0.4 }}
                  >
                    <label htmlFor="career-position" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      Position Applying For
                    </label>
                    <select
                      id="career-position"
                      name="position"
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm cursor-pointer"
                      required
                    >
                      <option value="">Select a position</option>
                      {jobTitles.map((title) => (
                        <option key={title} value={title}>{title}</option>
                      ))}
                      <option value="General Application">General Application</option>
                    </select>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.54, duration: 0.4 }}
                  >
                    <label htmlFor="career-linkedin" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      LinkedIn / Portfolio URL
                    </label>
                    <input
                      type="url"
                      id="career-linkedin"
                      name="portfolio_url"
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm"
                      placeholder="https://linkedin.com/in/..."
                    />
                  </motion.div>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.57, duration: 0.4 }}
                >
                  <label htmlFor="career-resume" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                    Resume / CV Link (Google Drive, Dropbox, etc.)
                  </label>
                  <input
                    type="url"
                    id="career-resume"
                    name="resume_url"
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm"
                    placeholder="https://drive.google.com/..."
                    required
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.6, duration: 0.4 }}
                >
                  <label htmlFor="career-message" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                    Cover Message <span className="text-gray-400">({coverMessage.length}/500)</span>
                  </label>
                  <textarea
                    id="career-message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    value={coverMessage}
                    onChange={(e) => setCoverMessage(e.target.value)}
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm resize-none"
                    placeholder="Tell us why you want to join and what makes you a great fit..."
                  ></textarea>
                </motion.div>

                <motion.button
                  initial={{ opacity: 0, y: 15 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.63, duration: 0.4 }}
                  type="submit"
                  className="w-full py-3 md:py-4 bg-primary-500 text-white text-sm md:text-base font-semibold rounded-lg hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
                >
                  Submit Application
                  <i className="ri-send-plane-line ml-2 group-hover:translate-x-1 transition-transform duration-300 inline-block"></i>
                </motion.button>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={inView ? { opacity: 1 } : {}}
                  transition={{ delay: 0.66, duration: 0.4 }}
                  className="text-xs text-center text-gray-500 mt-3 md:mt-4"
                >
                  By submitting, you agree to our{' '}
                  <Link to="/terms" className="text-primary-600 hover:underline cursor-pointer">Terms</Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-primary-600 hover:underline cursor-pointer">Privacy Policy</Link>
                </motion.p>
              </form>
            </div>
          </motion.div>
        </div>

        {/* Explore Bridge */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5, ...springTransition }}
          className="mt-16 md:mt-24"
        >
          <div className="bg-white rounded-2xl md:rounded-3xl border border-gray-100 p-6 md:p-8 lg:p-10 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 md:mb-8">
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-navy-900 mb-1">Learn More About Working With Us</h3>
                <p className="text-sm md:text-base text-gray-600">
                  Discover how we operate and see the impact our teams make for clients worldwide.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              <Link
                to="/how-it-works"
                className="group flex items-center gap-4 p-5 md:p-6 bg-gray-50 rounded-xl md:rounded-2xl border border-gray-100 hover:border-primary-300 hover:shadow-md transition-all duration-300 cursor-pointer"
              >
                <div className="w-100 h-28 md:w-100 md:h-28 flex items-center justify-center bg-primary-500 rounded-xl group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                  <i className="ri-route-line text-xl md:text-2xl text-white"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-base md:text-lg font-bold text-navy-900 group-hover:text-primary-700 transition-colors mb-0.5">
                    See Our Process
                  </h4>
                  <p className="text-sm text-gray-500">
                    From first contact to deployed engineer in days, not weeks.
                  </p>
                </div>
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-gray-200 text-navy-900 group-hover:bg-primary-500 group-hover:text-white group-hover:border-primary-500 transition-all duration-300 flex-shrink-0">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </Link>
              <Link
                to="/case-studies"
                className="group flex items-center gap-4 p-5 md:p-6 bg-gray-50 rounded-xl md:rounded-2xl border border-gray-100 hover:border-primary-300 hover:shadow-md transition-all duration-300 cursor-pointer"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-navy-900 rounded-xl group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                  <i className="ri-folder-chart-line text-xl md:text-2xl text-white"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-base md:text-lg font-bold text-navy-900 group-hover:text-primary-700 transition-colors mb-0.5">
                    View Client Success Stories
                  </h4>
                  <p className="text-sm text-gray-500">
                    Real outcomes from real engagements across industries.
                  </p>
                </div>
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-gray-200 text-navy-900 group-hover:bg-primary-500 group-hover:text-white group-hover:border-primary-500 transition-all duration-300 flex-shrink-0">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}